import React, { useMemo } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import SearchItem from '@layout/header/SearchItem'
import { SearchableObject } from '@models/SearchableObject'
import { SearchCategory, SearchConfig, SuggestionResults } from '@services/SearchService'

interface Props {
  term: string
  results: SuggestionResults | null
  config: SearchConfig
  numItemSelected: number
}

const SearchResults: React.FC<Props> = ({ term, results, config, numItemSelected }: Props) => {
  const { t } = useTranslation()

  const items = useMemo(() => {
    let itemCount = 0
    return Object.keys(config).reduce((itemArr, configKey) => {
      itemArr.push({ type: 'heading', value: t(`section.${configKey}`), key: configKey })

      itemArr.push({
        type: 'result',
        category: configKey,
        title: term,
        id: `${configKey}-1`,
        itemNum: itemCount++,
      })
      if (results && results[configKey]) {
        results[configKey].forEach((result: SearchableObject) => {
          itemArr.push({
            type: 'result',
            category: configKey,
            title: result.title,
            id: result.id,
            itemNum: itemCount++,
          })
        })
      }

      return itemArr
    }, [] as Item[])
  }, [config, results, term])

  return (
    <div className="absolute left-0 top-full z-50 px-3 py-4 border-t border-gray dark:border-gray-border-dm sm:border-t-0 w-screen bg-white dark:bg-black-bg2 shadow-md sm:rounded-md sm:mt-5 sm:w-full sm:shadow-thick">
      {items.map((item, index) => {
        if (item.type === 'heading') {
          return (
            <p className={classNames('mx-1 font-medium', { 'mt-5': index !== 0 })} key={item.key}>
              {item.value}
            </p>
          )
        }
        return (
          <SearchItem
            category={item.category as SearchCategory}
            title={item.title}
            isSelected={item.itemNum === numItemSelected}
            key={item.id}
          />
        )
      })}
    </div>
  )
}

export default SearchResults

type Item = HeadingItem | ResultItem

type HeadingItem = {
  type: 'heading'
  value: string
  key: string
}

type ResultItem = {
  type: 'result'
  category: string
  title: string
  id: string
  itemNum: number
}
